// ========================================================================
// This file is auto-generated by the `yarn naptime:types` script
// ========================================================================
import NaptimeResource from 'bundles/naptimejs/resources/NaptimeResource';
import { CourseMaterialContentSummaryUngradedAssignment } from 'bundles/naptimejs/resources/__generated__/GuidedCourseNextStepsV1';

// ========================================================================
// Definitions for resource data types
// ========================================================================

export type DeadlineStatus = 'FAILED' | 'OVERDUE' | 'ONTRACK' | 'COMPLETED';

export interface CourseMaterialContentSummaryTeammateReview {
  typeName: 'teammateReview';
  definition: any;
}

export interface PlaceholderItemContentSummaryDefinition {
  gradingLatePenalty?: GradingLatePenalty | null;
  learnerMessage?: string | null;
}

export interface CourseMaterialContentSummaryPlaceholder {
  typeName: 'placeholder';
  definition: PlaceholderItemContentSummaryDefinition;
}

export interface SupplementSummaryDefinition {
  assetTypeName: string;
}

export interface CourseMaterialContentSummarySupplement {
  typeName: 'supplement';
  definition: SupplementSummaryDefinition;
}

export interface StaffGradedSummaryDefinition {
  isTeamSubmissionEnabled: boolean;
  standardProctorConfigurationId?: string | null;
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryStaffGraded {
  typeName: 'staffGraded';
  definition: StaffGradedSummaryDefinition;
}

export interface UngradedWidgetSummaryDefinition {
  supportsTouch: boolean;
  minWidthPx?: number | null;
  minHeightPx?: number | null;
}

export interface CourseMaterialContentSummaryUngradedWidget {
  typeName: 'ungradedWidget';
  definition: UngradedWidgetSummaryDefinition;
}

export interface CourseMaterialContentSummaryUngradedProgramming {
  typeName: 'ungradedProgramming';
  definition: any;
}

export interface StandardPeerSummaryDefinition {
  requiredReviewCount: number;
  isMentorGraded: boolean;
}

export interface CourseMaterialContentSummaryPeer {
  typeName: 'peer';
  definition: StandardPeerSummaryDefinition;
}

export interface CourseMaterialContentSummaryUngradedLti {
  typeName: 'ungradedLti';
  definition: any;
}

export interface GradedDiscussionPromptSummaryDefinition {
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryGradedDiscussionPrompt {
  typeName: 'gradedDiscussionPrompt';
  definition: GradedDiscussionPromptSummaryDefinition;
}

export interface LectureSummaryDefinition {
  duration?: number | null;
  hasInVideoAssessment: boolean;
}

export interface CourseMaterialContentSummaryLecture {
  typeName: 'lecture';
  definition: LectureSummaryDefinition;
}

export interface GradedWidgetSummaryDefinition {
  supportsTouch: boolean;
  minWidthPx?: number | null;
  minHeightPx?: number | null;
}

export interface CourseMaterialContentSummaryGradedWidget {
  typeName: 'gradedWidget';
  definition: GradedWidgetSummaryDefinition;
}

export interface CourseMaterialContentSummarySlideshow {
  typeName: 'slideshow';
  definition: any;
}

export interface GradedPeerSummaryDefinition {
  requiredReviewCount: number;
  isMentorGraded: boolean;
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryGradedPeer {
  typeName: 'gradedPeer';
  definition: GradedPeerSummaryDefinition;
}

export interface AssessOpenSinglePageSummaryDefinition {
  questionCount?: number | null;
  containsWidgetQuestions: boolean;
}

export interface CourseMaterialContentSummaryAssessOpenSinglePage {
  typeName: 'assessOpenSinglePage';
  definition: AssessOpenSinglePageSummaryDefinition;
}

export interface AttemptRateLimit {
  allowedSubmissionsPerInterval: number;
  allowedSubmissionsInterval: number;
}

export interface FormativeQuizSummaryDefinition {
  questionCount: number;
  passingFraction: number;
  containsWidgetQuestions: boolean;
  maxScore: number;
  passingScore: number;
  standardProctorConfigurationId?: string | null;
  lockingConfiguration?: AttemptRateLimit | null;
}

export interface CourseMaterialContentSummaryQuiz {
  typeName: 'quiz';
  definition: FormativeQuizSummaryDefinition;
}

export interface ClosedPeerSummaryDefinition {
  requiredReviewCount: number;
  isMentorGraded: boolean;
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryClosedPeer {
  typeName: 'closedPeer';
  definition: ClosedPeerSummaryDefinition;
}

export interface CourseMaterialContentSummaryWorkspaceLauncher {
  typeName: 'workspaceLauncher';
  definition: any;
}

export interface SingleQuestionSubmitQuizSummaryDefinition {
  questionCount?: number | null;
  containsWidgetQuestions: boolean;
}

export interface CourseMaterialContentSummarySingleQuestionSubmit {
  typeName: 'singleQuestionSubmit';
  definition: SingleQuestionSubmitQuizSummaryDefinition;
}

export interface CourseMaterialContentSummaryWiseFlow {
  typeName: 'wiseFlow';
  definition: any;
}

export interface CourseMaterialContentSummaryUngradedLab {
  typeName: 'ungradedLab';
  definition: any;
}

export interface ImmediateProgrammingSummaryDefinition {
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryProgramming {
  typeName: 'programming';
  definition: ImmediateProgrammingSummaryDefinition;
}

export interface FixedGradingLatePenalty {
  penalty: number;
}

export interface GradingLatePenaltyFixedGradingLatePenalty {
  typeName: 'fixedGradingLatePenalty';
  definition: FixedGradingLatePenalty;
}

export type PenaltyIncrementMode =
  | 'APPLY_PENALTY_FRACTION_ON_AWARDED_SCORE'
  | 'DECREASE_PENALTY_POINTS_ON_AWARDED_SCORE';

export type PeriodType = 'ZERO' | 'MINUTE' | 'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'INFINITY';

export interface TimePeriod {
  count: number;
  periodType: PeriodType;
}

export interface LatePenaltyIncrement {
  penalty: number;
  penaltyMode: PenaltyIncrementMode;
  applyEvery: TimePeriod;
  maxApplications?: number | null;
}

export interface LatePenaltyScoreCap {
  applyAfter: TimePeriod;
  maxFinalScore: number;
}

export interface LatePenaltyMinScoreThreshold {
  minScoreForLatePenaltyApplication: number;
}

export interface CompoundLatePenalty {
  incrementalPenalty?: LatePenaltyIncrement | null;
  maxAllowedScore?: LatePenaltyScoreCap | null;
  minScorethreshold?: LatePenaltyMinScoreThreshold | null;
}

export interface GradingLatePenaltyCompoundLatePenalty {
  typeName: 'compoundLatePenalty';
  definition: CompoundLatePenalty;
}

export interface IncrementalGradingLatePenalty {
  penalty: number;
}

export interface GradingLatePenaltyIncrementalGradingLatePenalty {
  typeName: 'incrementalGradingLatePenalty';
  definition: IncrementalGradingLatePenalty;
}

export type GradingLatePenalty =
  | GradingLatePenaltyFixedGradingLatePenalty
  | GradingLatePenaltyCompoundLatePenalty
  | GradingLatePenaltyIncrementalGradingLatePenalty;

export type GradingLatePenaltyTypeName =
  | 'fixedGradingLatePenalty'
  | 'compoundLatePenalty'
  | 'incrementalGradingLatePenalty';

export interface SummativeQuizSummaryDefinition {
  questionCount: number;
  passingFraction: number;
  containsWidgetQuestions: boolean;
  maxScore: number;
  passingScore: number;
  standardProctorConfigurationId?: string | null;
  allowedSubmissionsPerInterval: number;
  allowedSubmissionsInterval: number;
  lockingConfiguration?: AttemptRateLimit | null;
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryExam {
  typeName: 'exam';
  definition: SummativeQuizSummaryDefinition;
}

export interface PhasedPeerSummaryDefinition {
  requiredReviewCount: number;
  isMentorGraded: boolean;
  reviewDeadlineOffset: number;
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryPhasedPeer {
  typeName: 'phasedPeer';
  definition: PhasedPeerSummaryDefinition;
}

export interface NotebookSummaryDefinition {
  isGradedByItem: boolean;
}

export interface CourseMaterialContentSummaryNotebook {
  typeName: 'notebook';
  definition: NotebookSummaryDefinition;
}

export interface GradedLtiSummaryDefinition {
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryGradedLti {
  typeName: 'gradedLti';
  definition: GradedLtiSummaryDefinition;
}

export interface GradedProgrammingSummaryDefinition {
  gradingLatePenalty?: GradingLatePenalty | null;
}

export interface CourseMaterialContentSummaryGradedProgramming {
  typeName: 'gradedProgramming';
  definition: GradedProgrammingSummaryDefinition;
}

export interface CourseMaterialContentSummaryDiscussionPrompt {
  typeName: 'discussionPrompt';
  definition: any;
}

export type CourseMaterialContentSummary =
  | CourseMaterialContentSummaryTeammateReview
  | CourseMaterialContentSummaryPlaceholder
  | CourseMaterialContentSummarySupplement
  | CourseMaterialContentSummaryStaffGraded
  | CourseMaterialContentSummaryUngradedAssignment
  | CourseMaterialContentSummaryUngradedWidget
  | CourseMaterialContentSummaryUngradedProgramming
  | CourseMaterialContentSummaryPeer
  | CourseMaterialContentSummaryUngradedLti
  | CourseMaterialContentSummaryGradedDiscussionPrompt
  | CourseMaterialContentSummaryLecture
  | CourseMaterialContentSummaryGradedWidget
  | CourseMaterialContentSummarySlideshow
  | CourseMaterialContentSummaryGradedPeer
  | CourseMaterialContentSummaryAssessOpenSinglePage
  | CourseMaterialContentSummaryQuiz
  | CourseMaterialContentSummaryClosedPeer
  | CourseMaterialContentSummaryWorkspaceLauncher
  | CourseMaterialContentSummarySingleQuestionSubmit
  | CourseMaterialContentSummaryWiseFlow
  | CourseMaterialContentSummaryUngradedLab
  | CourseMaterialContentSummaryProgramming
  | CourseMaterialContentSummaryExam
  | CourseMaterialContentSummaryPhasedPeer
  | CourseMaterialContentSummaryNotebook
  | CourseMaterialContentSummaryGradedLti
  | CourseMaterialContentSummaryGradedProgramming
  | CourseMaterialContentSummaryDiscussionPrompt;

export type CourseMaterialContentSummaryTypeName =
  | 'teammateReview'
  | 'placeholder'
  | 'supplement'
  | 'staffGraded'
  | 'ungradedAssignment'
  | 'ungradedWidget'
  | 'ungradedProgramming'
  | 'peer'
  | 'ungradedLti'
  | 'gradedDiscussionPrompt'
  | 'lecture'
  | 'gradedWidget'
  | 'slideshow'
  | 'gradedPeer'
  | 'assessOpenSinglePage'
  | 'quiz'
  | 'closedPeer'
  | 'workspaceLauncher'
  | 'singleQuestionSubmit'
  | 'wiseFlow'
  | 'ungradedLab'
  | 'programming'
  | 'exam'
  | 'phasedPeer'
  | 'notebook'
  | 'gradedLti'
  | 'gradedProgramming'
  | 'discussionPrompt';

export interface ItemGroup {
  id: string;
  requiredPassedCount: number;
  passableItemGroupChoiceIds: Array<string>;
}

export interface CompoundLatePenaltyRatio {
  ratio: number;
  penaltyContext: CompoundLatePenalty;
  submissionDeadline: number;
  submissionTime: number;
}

export interface LatePenaltyRatioCompoundLatePenaltyRatio {
  typeName: 'compoundLatePenaltyRatio';
  definition: CompoundLatePenaltyRatio;
}

export interface FixedLatePenaltyRatio {
  ratio: number;
}

export interface LatePenaltyRatioFixedLatePenaltyRatio {
  typeName: 'fixedLatePenaltyRatio';
  definition: FixedLatePenaltyRatio;
}

export interface IncrementalLatePenaltyRatio {
  ratio: number;
  duration: number;
}

export interface LatePenaltyRatioIncrementalLatePenaltyRatio {
  typeName: 'incrementalLatePenaltyRatio';
  definition: IncrementalLatePenaltyRatio;
}

export type LatePenaltyRatio =
  | LatePenaltyRatioCompoundLatePenaltyRatio
  | LatePenaltyRatioFixedLatePenaltyRatio
  | LatePenaltyRatioIncrementalLatePenaltyRatio;

export type LatePenaltyRatioTypeName =
  | 'compoundLatePenaltyRatio'
  | 'fixedLatePenaltyRatio'
  | 'incrementalLatePenaltyRatio';

export interface ItemOutcome {
  grade: number;
  isPassed: boolean;
  latePenaltyRatio?: LatePenaltyRatio | null;
}

export type ItemLockStatus = 'LOCKED_FOR_SUBMITTING' | 'LOCKED_FULLY';

export type ItemLockedReasonCode =
  | 'PREMIUM'
  | 'PASSABLE_ITEM_COMPLETION'
  | 'PREMIUM_ITEM'
  | 'SESSION_PREVIEW'
  | 'SESSION_ENDED'
  | 'SESSION_ENDED_FOR_FLEXIBLE_SCHEDULE'
  | 'ENROLLMENT_PREVIEW'
  | 'RESTRICTED_AUDIT'
  | 'TIMED_RELEASE_CONTENT'
  | 'RESITTING_ITEM_LOCK_OVERRIDE';

export interface ItemLockState {
  lockStatus: ItemLockStatus;
  reasonCode: ItemLockedReasonCode;
}

export interface TimedReleaseContentLockInfo {
  unlockedTimeStart?: number | null;
  unlockedTimeEnd?: number | null;
  timedItemLockCustomMessage?: string | null;
}

export interface ItemLockInfoTimedReleaseContentLockInfo {
  typeName: 'timedReleaseContentLockInfo';
  definition: TimedReleaseContentLockInfo;
}

export type ItemLockInfo = ItemLockInfoTimedReleaseContentLockInfo;

export type ItemLockInfoTypeName = 'timedReleaseContentLockInfo';

export interface ItemLockSummary {
  lockState: ItemLockState;
  lockInfo?: ItemLockInfo | null;
  isPreviewableByCourseStaff?: boolean | null;
}

export interface Item {
  id: string;
  name: string;
  resourcePath: string;
  trackId: string;
  isLocked: boolean;
  isOptional: boolean;
  timeCommitment: number;
  deadline?: number | null;
  contentSummary: CourseMaterialContentSummary;
  itemDeadlineStatus: DeadlineStatus;
  itemGroup?: ItemGroup | null;
  computedOutcome?: ItemOutcome | null;
  computedProgressState: string;
  gradingLatePenalty?: GradingLatePenalty | null;
  itemLockSummary?: ItemLockSummary | null;
}

export interface EstimateTimeProgress {
  totalDuration: number;
  remainingDuration: number;
  totalCount: number;
  remainingCount: number;
  nextItem?: Item | null;
}

export interface GuidedWeekEstimateTimeProgress {
  totalItemsProgress: EstimateTimeProgress;
  passableItemsProgress: EstimateTimeProgress;
  nonPassableItemsProgress: EstimateTimeProgress;
}

export interface GuidedModuleEstimateTimeProgress {
  lecturesProgress: EstimateTimeProgress;
  readingsProgress: EstimateTimeProgress;
  nonGradedAssessmentsProgress: EstimateTimeProgress;
  otherNonPassableItemsProgress: EstimateTimeProgress;
  totalItemsProgress: EstimateTimeProgress;
}

export interface GuidedModule {
  id: string;
  name: string;
  items: Array<Item>;
  moduleTimeProgress: GuidedModuleEstimateTimeProgress;
}

export interface CourseWeekProgress {
  learnerMaterialWeekNumber: string;
  deadline?: number | null;
  isCurrentWeekBySchedule: boolean;
  weekStatus: DeadlineStatus;
  previousWeekStatus?: DeadlineStatus | null;
  estimateTimeProgress: GuidedWeekEstimateTimeProgress;
  modules: Array<GuidedModule>;
}

export interface GuidedCourseSessionProgressesV1 {
  id: string;
  courseId: string;
  courseSlug: string;
  sessionId: string;
  sessionStartedAt: number;
  sessionEndedAt: number;
  startedAt: number;
  endedAt: number;
  currentWeekByUserProgress: number;
  weeks: Array<CourseWeekProgress>;
  hasSessionModuleDeadlines: boolean;
  courseProgressState: string;
}

// ========================================================================
// Definitions for naptime-link
// ========================================================================

export interface GuidedCourseSessionProgressesV1Connection {
  elements: Array<GuidedCourseSessionProgressesV1> | null;
}

// ========================================================================
// Definitions for naptime-link queries
// ========================================================================

export interface GuidedCourseSessionProgressesV1MultiGetQuery {
  GuidedCourseSessionProgressesV1: {
    multiGet: GuidedCourseSessionProgressesV1Connection | null;
  } | null;
}

export interface GuidedCourseSessionProgressesV1MultiGetQueryVariables {
  ids: string;
  includes?: string | null;
}

// ========================================================================
// Definitions for naptime-js
// ========================================================================

// Recursively applies Partial<T> to supported culling by the `fields` param.
type PartialConstruction<T> = {
  [TKey in keyof T]?: T[TKey] extends Array<infer TElement>
    ? Array<PartialConstruction<TElement>>
    : T[TKey] extends object
    ? PartialConstruction<T[TKey]>
    : T[TKey];
};

export class GuidedCourseSessionProgressesV1NaptimeResource extends NaptimeResource {
  static RESOURCE_NAME = 'guidedCourseSessionProgresses.v1' as const;

  declare id: string;

  declare courseId: string;

  declare courseSlug: string;

  declare sessionId: string;

  declare sessionStartedAt: number;

  declare sessionEndedAt: number;

  declare startedAt: number;

  declare endedAt: number;

  declare currentWeekByUserProgress: number;

  declare weeks: Array<CourseWeekProgress>;

  declare hasSessionModuleDeadlines: boolean;

  declare courseProgressState: string;

  // Intentionally "useless" to supply params type
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(params: PartialConstruction<GuidedCourseSessionProgressesV1>) {
    super(params);
  }
}
